import type { DealResponseDto } from "../../types/types";

/**
 * Deal status changes (accepted / declined / withdrawn) are stored as ordinary
 * chat messages. Instead of sending translated English text we send a small
 * structured token, and every viewer translates it in their own language at
 * render time. The backend only stores it as text.
 */
export type DealStatusEvent = "ACCEPTED" | "REJECTED" | "CANCELLED";

export interface DealStatusPayload {
  v: 1;
  event: DealStatusEvent;
  /** Internal deal id, used for navigation. */
  id: string;
  /** Display id, e.g. DL-2026-000029. */
  dealId: string;
  /** Raw enum, e.g. FIGHT_SINGLE. */
  dealType?: string;
  sport?: string;
  amount?: string | number;
  currency?: string;
}

export type ParsedDealStatus =
  | { kind: "structured"; payload: DealStatusPayload }
  | { kind: "legacy"; text: string; id: string | null };

const PREFIX = "__DEAL_STATUS__";
const EVENTS: readonly DealStatusEvent[] = ["ACCEPTED", "REJECTED", "CANCELLED"];

// LEGACY: messages already stored in the DB by older app versions (English only).
// Delete this block (and the "legacy" branch) once those messages are no longer relevant.
const LEGACY_ACTION_REGEX = /has (accepted|declined|withdrawn) the deal:/i;
const LEGACY_DEAL_ID_REGEX = /\s*Deal ID:\s*([A-Za-z0-9_-]+)\s*$/i;
const LEGACY_EVENT_BY_ACTION: Record<string, DealStatusEvent> = {
  accepted: "ACCEPTED",
  declined: "REJECTED",
  withdrawn: "CANCELLED",
};

/** Guards against "nan" / "undefined" / "null" ids that were stored by a past bug. */
export const cleanDealId = (value?: string | null): string | null => {
  const text = String(value ?? "").trim();
  const lower = text.toLowerCase();
  if (!text || lower === "nan" || lower === "undefined" || lower === "null") {
    return null;
  }
  return text;
};

const optionalString = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value : undefined;

export const encodeDealStatusMessage = (
  deal: DealResponseDto,
  event: DealStatusEvent,
): string => {
  const core = deal.formData?.core;
  const details = deal.formData?.details;
  const amount = core?.baseAmount;

  const payload: DealStatusPayload = {
    v: 1,
    event,
    id: String(deal.id),
    dealId: deal.dealId,
    dealType: deal.dealType || undefined,
    sport: optionalString(details?.sport),
    amount:
      typeof amount === "number" || typeof amount === "string"
        ? amount
        : undefined,
    currency: optionalString(core?.currency),
  };

  return PREFIX + JSON.stringify(payload);
};

const parseStructured = (text: string): DealStatusPayload | null => {
  try {
    const data = JSON.parse(text.slice(PREFIX.length));
    if (!data || typeof data !== "object") return null;
    if (data.v !== 1 || !EVENTS.includes(data.event)) return null;
    if (typeof data.id !== "string" || typeof data.dealId !== "string") {
      return null;
    }
    return {
      v: 1,
      event: data.event,
      id: data.id,
      dealId: data.dealId,
      dealType: optionalString(data.dealType),
      sport: optionalString(data.sport),
      amount:
        typeof data.amount === "number" || typeof data.amount === "string"
          ? data.amount
          : undefined,
      currency: optionalString(data.currency),
    };
  } catch {
    return null;
  }
};

/** Returns null when the text is not a deal status message. */
export const parseDealStatusMessage = (
  text?: string | null,
): ParsedDealStatus | null => {
  if (!text) return null;

  if (text.startsWith(PREFIX)) {
    const payload = parseStructured(text);
    return payload ? { kind: "structured", payload } : null;
  }

  // LEGACY branch, see note above.
  const idMatch = text.match(LEGACY_DEAL_ID_REGEX);
  if (idMatch && LEGACY_ACTION_REGEX.test(text)) {
    return {
      kind: "legacy",
      text: text.replace(LEGACY_DEAL_ID_REGEX, "").trim(),
      id: cleanDealId(idMatch[1]),
    };
  }

  return null;
};

const PREVIEW_KEY_BY_EVENT: Record<DealStatusEvent, string> = {
  ACCEPTED: "system.previewAccepted",
  REJECTED: "system.previewRejected",
  CANCELLED: "system.previewWithdrawn",
};

/** Short translated text for the sidebar. Returns null for ordinary messages. */
export const getDealStatusPreview = (
  text: string | null | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string | null => {
  const parsed = parseDealStatusMessage(text);
  if (!parsed) return null;

  if (parsed.kind === "structured") {
    const { event, dealId } = parsed.payload;
    return t(PREVIEW_KEY_BY_EVENT[event], { dealId }).trim();
  }

  // LEGACY: pick the event and the display id out of the English sentence.
  const action = parsed.text.match(LEGACY_ACTION_REGEX)?.[1]?.toLowerCase();
  const event = action ? LEGACY_EVENT_BY_ACTION[action] : undefined;
  const dealId = parsed.text.match(/\bDL-[A-Za-z0-9-]+/i)?.[0] ?? "";
  return event ? t(PREVIEW_KEY_BY_EVENT[event], { dealId }).trim() : null;
};
